'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Save, AlertCircle, CheckCircle, Egg, Calculator, Shield } from 'lucide-react';
import Link from 'next/link';
import {
  EGG_FARMS, EGG_HOUSES_BY_FARM, EGG_FLOCKS,
  calcHenDayProduction, calcRate, calcTrays
} from '../../../components/eggProductionData';
import { useRole } from '@/lib/useRole';

interface FormData {
  productionDate: string;
  farm: string;
  house: string;
  flockId: string;
  ageWeeks: string;
  openingBirdPopulation: string;
  morningCollection: string;
  middayCollection: string;
  eveningCollection: string;
  otherCollection: string;
  goodEggs: string;
  crackedEggs: string;
  dirtyEggs: string;
  smallEggs: string;
  mediumEggs: string;
  largeEggs: string;
  extraLargeEggs: string;
  rejectedEggs: string;
  hatchingEggs: string;
  doubleYolkEggs: string;
  otherEggs: string;
  traySize: string;
  productionNotes: string;
  observedProblems: string;
  weatherNotes: string;
  recordedBy: string;
}

const INITIAL: FormData = {
  productionDate: new Date().toISOString().split('T')[0],
  farm: 'Idudi Poultry Farm',
  house: '',
  flockId: '',
  ageWeeks: '',
  openingBirdPopulation: '',
  morningCollection: '',
  middayCollection: '',
  eveningCollection: '',
  otherCollection: '0',
  goodEggs: '',
  crackedEggs: '0',
  dirtyEggs: '0',
  smallEggs: '0',
  mediumEggs: '0',
  largeEggs: '0',
  extraLargeEggs: '0',
  rejectedEggs: '0',
  hatchingEggs: '0',
  doubleYolkEggs: '0',
  otherEggs: '0',
  traySize: '30',
  productionNotes: '',
  observedProblems: '',
  weatherNotes: '',
  recordedBy: 'Sarah Namukasa',
};

function num(v: string) { return parseInt(v, 10) || 0; }

export default function NewEggRecordContent() {
  const router = useRouter();
  const [form, setForm] = useState<FormData>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { can } = useRole();

  if (!can('egg_production.create')) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <Shield size={28} className="text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to create production records.</p>
        <Link href="/egg-production/records" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Records
        </Link>
      </div>
    );
  }

  const set = useCallback((key: keyof FormData, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }, []);

  // Derived values
  const totalEggs = num(form.morningCollection) + num(form.middayCollection) + num(form.eveningCollection) + num(form.otherCollection);
  const classifiedTotal = num(form.goodEggs) + num(form.crackedEggs) + num(form.dirtyEggs) + num(form.rejectedEggs) + num(form.hatchingEggs) + num(form.doubleYolkEggs) + num(form.otherEggs);
  const classifiedExcess = classifiedTotal > totalEggs;
  const trayCalc = calcTrays(num(form.goodEggs), num(form.traySize) || 30);
  const hdp = calcHenDayProduction(totalEggs, num(form.openingBirdPopulation));
  const goodRate = calcRate(num(form.goodEggs), totalEggs);
  const crackRate = calcRate(num(form.crackedEggs), totalEggs);
  const rejectRate = calcRate(num(form.rejectedEggs), totalEggs);

  const availableHouses = form.farm ? (EGG_HOUSES_BY_FARM[form.farm] ?? []) : [];
  const availableFlocks = EGG_FLOCKS.filter(f => f.farm === form.farm && (!form.house || f.house === form.house));

  function handleFlockSelect(flockId: string) {
    const flock = EGG_FLOCKS.find(f => f.id === flockId);
    if (flock) {
      set('flockId', flockId);
      set('house', flock.house);
      set('ageWeeks', String(flock.ageWeeks));
      set('openingBirdPopulation', String(flock.currentBirds));
    }
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormData, string>> = {};
    if (!form.productionDate) errs.productionDate = 'Date is required';
    if (!form.farm) errs.farm = 'Farm is required';
    if (!form.house) errs.house = 'House is required';
    if (!form.flockId) errs.flockId = 'Flock is required';
    if (!form.openingBirdPopulation || num(form.openingBirdPopulation) <= 0) errs.openingBirdPopulation = 'Bird population must be > 0';
    if (totalEggs === 0) errs.morningCollection = 'At least one collection must be > 0';
    if (classifiedExcess) errs.goodEggs = `Classified total (${classifiedTotal}) exceeds collected eggs (${totalEggs})`;
    if (!form.recordedBy) errs.recordedBy = 'Recorded by is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave(status: 'draft' | 'submitted') {
    if (!validate()) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    setSaved(true);
    setTimeout(() => router.push('/egg-production/records'), 1500);
  }

  const inputCls = (key: keyof FormData) =>
    `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${errors[key] ? 'border-red-400 bg-red-50' : 'border-gray-200'}`;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link href="/egg-production/records" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Record Daily Egg Production</h1>
          <p className="text-sm text-gray-500 mt-0.5">Enter today's egg collection data for a flock.</p>
        </div>
      </div>

      {saved && (
        <div className="mb-4 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-xl">
          <CheckCircle size={16} />
          Record saved successfully. Redirecting...
        </div>
      )}

      <div className="space-y-6">
        {/* Section A: Production Details */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">A</span>
            Production Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Production Date *</label>
              <input type="date" value={form.productionDate} onChange={e => set('productionDate', e.target.value)} className={inputCls('productionDate')} />
              {errors.productionDate && <p className="text-xs text-red-500 mt-1">{errors.productionDate}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Farm *</label>
              <select value={form.farm} onChange={e => { set('farm', e.target.value); set('house', ''); set('flockId', ''); }} className={inputCls('farm')}>
                <option value="">Select Farm</option>
                {EGG_FARMS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Flock *</label>
              <select value={form.flockId} onChange={e => handleFlockSelect(e.target.value)} className={inputCls('flockId')}>
                <option value="">Select Flock</option>
                {availableFlocks.map(f => <option key={f.id} value={f.id}>{f.flockNumber} — {f.flockName}</option>)}
              </select>
              {errors.flockId && <p className="text-xs text-red-500 mt-1">{errors.flockId}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Poultry House *</label>
              <select value={form.house} onChange={e => set('house', e.target.value)} className={inputCls('house')}>
                <option value="">Select House</option>
                {availableHouses.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              {errors.house && <p className="text-xs text-red-500 mt-1">{errors.house}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Age (Weeks)</label>
              <input type="number" value={form.ageWeeks} onChange={e => set('ageWeeks', e.target.value)} className={inputCls('ageWeeks')} placeholder="e.g. 33" min="0" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Opening Bird Population *</label>
              <input type="number" value={form.openingBirdPopulation} onChange={e => set('openingBirdPopulation', e.target.value)} className={inputCls('openingBirdPopulation')} placeholder="e.g. 3150" min="1" />
              {errors.openingBirdPopulation && <p className="text-xs text-red-500 mt-1">{errors.openingBirdPopulation}</p>}
            </div>
          </div>
        </div>

        {/* Section B: Egg Collection */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">B</span>
            Egg Collection
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {[
              { key: 'morningCollection' as keyof FormData, label: 'Morning' },
              { key: 'middayCollection' as keyof FormData, label: 'Midday' },
              { key: 'eveningCollection' as keyof FormData, label: 'Evening' },
              { key: 'otherCollection' as keyof FormData, label: 'Other' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{label} Collection</label>
                <input type="number" value={form[key]} onChange={e => set(key, e.target.value)} className={inputCls(key)} placeholder="0" min="0" />
                {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
            <Egg size={18} className="text-green-600" />
            <div>
              <span className="text-xs text-green-700 font-medium">Total Eggs Collected</span>
              <span className="ml-3 text-xl font-bold text-green-800">{totalEggs.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Section C: Egg Classification */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">C</span>
            Egg Classification
          </h2>
          <p className="text-xs text-gray-400 mb-4">Sum of classified eggs must not exceed total collected ({totalEggs.toLocaleString()})</p>
          {classifiedExcess && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg mb-4">
              <AlertCircle size={14} />
              Classified total ({classifiedTotal}) exceeds collected eggs ({totalEggs}). Please correct the values.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { key: 'goodEggs' as keyof FormData, label: 'Good Eggs', color: 'text-green-600' },
              { key: 'crackedEggs' as keyof FormData, label: 'Cracked Eggs', color: 'text-orange-500' },
              { key: 'dirtyEggs' as keyof FormData, label: 'Dirty Eggs', color: 'text-yellow-600' },
              { key: 'smallEggs' as keyof FormData, label: 'Small Eggs', color: 'text-gray-600' },
              { key: 'mediumEggs' as keyof FormData, label: 'Medium Eggs', color: 'text-gray-600' },
              { key: 'largeEggs' as keyof FormData, label: 'Large Eggs', color: 'text-gray-600' },
              { key: 'extraLargeEggs' as keyof FormData, label: 'Extra Large', color: 'text-gray-600' },
              { key: 'rejectedEggs' as keyof FormData, label: 'Rejected Eggs', color: 'text-red-500' },
              { key: 'hatchingEggs' as keyof FormData, label: 'Hatching Eggs', color: 'text-purple-600' },
              { key: 'doubleYolkEggs' as keyof FormData, label: 'Double Yolk', color: 'text-gray-600' },
              { key: 'otherEggs' as keyof FormData, label: 'Other Eggs', color: 'text-gray-600' },
            ].map(({ key, label, color }) => (
              <div key={key}>
                <label className={`block text-xs font-semibold mb-1 ${color}`}>{label}</label>
                <input type="number" value={form[key]} onChange={e => set(key, e.target.value)} className={inputCls(key)} placeholder="0" min="0" />
                {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <span className="text-xs text-gray-500 font-medium">Classified Total:</span>
            <span className={`text-base font-bold ${classifiedExcess ? 'text-red-600' : 'text-gray-900'}`}>{classifiedTotal.toLocaleString()}</span>
            <span className="text-xs text-gray-400">/ {totalEggs.toLocaleString()} collected</span>
          </div>
        </div>

        {/* Section D: Tray Calculation */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">D</span>
            Egg Trays
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tray Size (eggs per tray)</label>
              <select value={form.traySize} onChange={e => set('traySize', e.target.value)} className={inputCls('traySize')}>
                <option value="30">30 eggs</option>
                <option value="12">12 eggs</option>
                <option value="6">6 eggs</option>
              </select>
            </div>
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <Calculator size={16} className="text-blue-600" />
              <div>
                <p className="text-xs text-blue-600 font-medium">Full Trays</p>
                <p className="text-xl font-bold text-blue-800">{trayCalc.fullTrays}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
              <Egg size={16} className="text-gray-500" />
              <div>
                <p className="text-xs text-gray-500 font-medium">Loose Eggs</p>
                <p className="text-xl font-bold text-gray-800">{trayCalc.looseEggs}</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400">Based on Good Eggs ({num(form.goodEggs).toLocaleString()}) ÷ Tray Size ({form.traySize})</p>
        </div>

        {/* Auto Calculations */}
        {totalEggs > 0 && num(form.openingBirdPopulation) > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calculator size={16} className="text-green-600" />
              Automatic Calculations
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Hen-Day %', value: `${hdp}%`, color: hdp >= 90 ? 'text-green-600' : hdp >= 80 ? 'text-amber-600' : 'text-red-500' },
                { label: 'Good Egg Rate', value: `${goodRate}%`, color: 'text-green-600' },
                { label: 'Crack Rate', value: `${crackRate}%`, color: crackRate > 3 ? 'text-red-500' : 'text-gray-700' },
                { label: 'Rejection Rate', value: `${rejectRate}%`, color: rejectRate > 5 ? 'text-red-500' : 'text-gray-700' },
                { label: 'Dirty Egg Rate', value: `${calcRate(num(form.dirtyEggs), totalEggs)}%`, color: 'text-gray-700' },
                { label: 'Eggs/Bird', value: (totalEggs / num(form.openingBirdPopulation)).toFixed(2), color: 'text-gray-700' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 font-medium">{label}</p>
                  <p className={`text-lg font-bold mt-1 ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section E: Notes */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center font-bold">E</span>
            Notes & Observations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Production Notes</label>
              <textarea value={form.productionNotes} onChange={e => set('productionNotes', e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" placeholder="General production observations..." />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Observed Problems</label>
              <textarea value={form.observedProblems} onChange={e => set('observedProblems', e.target.value)} rows={3} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" placeholder="Any issues observed..." />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Weather / Environment Notes</label>
              <textarea value={form.weatherNotes} onChange={e => set('weatherNotes', e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" placeholder="Temperature, humidity, ventilation..." />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Recorded By *</label>
              <input type="text" value={form.recordedBy} onChange={e => set('recordedBy', e.target.value)} className={inputCls('recordedBy')} placeholder="Full name" />
              {errors.recordedBy && <p className="text-xs text-red-500 mt-1">{errors.recordedBy}</p>}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => handleSave('submitted')}
            disabled={saving || saved}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-sm disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? 'Saving...' : 'Save Record'}
          </button>
          <button
            onClick={() => handleSave('draft')}
            disabled={saving || saved}
            className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            Save Draft
          </button>
          <Link href="/egg-production/records" className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}
